import PageHeader from "@/components/common/PageHeader";
import { backgroundDbWorkRefetchIntervalMs } from "@/lib/background-db-work-pause";
import {
  monitoringService,
  type HealthGridTenant,
} from "@/services/monitoring.service";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Eye,
  Search,
  ShieldAlert,
  Users,
  Wifi,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const RISK_CONFIG = {
  healthy: {
    label: "Healthy",
    color: "bg-emerald-100 text-emerald-700 border-emerald-200",
    dotColor: "bg-emerald-500",
    icon: CheckCircle2,
  },
  monitor: {
    label: "Monitor",
    color: "bg-amber-100 text-amber-700 border-amber-200",
    dotColor: "bg-amber-500",
    icon: Eye,
  },
  at_risk: {
    label: "At Risk",
    color: "bg-orange-100 text-orange-700 border-orange-200",
    dotColor: "bg-orange-500",
    icon: AlertTriangle,
  },
  critical: {
    label: "Critical",
    color: "bg-red-100 text-red-700 border-red-200",
    dotColor: "bg-red-500",
    icon: ShieldAlert,
  },
  unscored: {
    label: "Unscored",
    color: "bg-slate-100 text-slate-500 border-slate-200",
    dotColor: "bg-slate-400",
    icon: Activity,
  },
};

function RiskBadge({ level }: { level: keyof typeof RISK_CONFIG }) {
  const config = RISK_CONFIG[level] || RISK_CONFIG.unscored;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${config.color}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${config.dotColor}`} />
      {config.label}
    </span>
  );
}

function HealthScoreCircle({ score }: { score: number | null }) {
  if (score === null) {
    return (
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
        <span className="text-xs font-medium text-slate-400">N/A</span>
      </div>
    );
  }

  let ringColor = "text-emerald-500";
  if (score < 40) ringColor = "text-red-500";
  else if (score < 60) ringColor = "text-orange-500";
  else if (score < 80) ringColor = "text-amber-500";

  const circumference = 2 * Math.PI * 20;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="relative flex h-12 w-12 items-center justify-center">
      <svg className="h-12 w-12 -rotate-90" viewBox="0 0 48 48">
        <circle
          cx="24"
          cy="24"
          r="20"
          stroke="currentColor"
          className="text-slate-100"
          strokeWidth="4"
          fill="none"
        />
        <circle
          cx="24"
          cy="24"
          r="20"
          stroke="currentColor"
          className={ringColor}
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <span className="absolute text-sm font-bold text-slate-900">{score}</span>
    </div>
  );
}

function SummaryCard({
  label,
  count,
  riskLevel,
  isActive,
  onClick,
}: {
  label: string;
  count: number;
  riskLevel: string;
  isActive: boolean;
  onClick: () => void;
}) {
  const config =
    RISK_CONFIG[riskLevel as keyof typeof RISK_CONFIG] || RISK_CONFIG.unscored;
  const Icon = config.icon;

  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center gap-2 rounded-2xl border p-4 transition-all ${
        isActive
          ? "border-slate-900 bg-slate-900 text-white shadow-lg"
          : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"
      }`}
    >
      <Icon
        className={`h-5 w-5 ${isActive ? "text-white" : config.dotColor.replace("bg-", "text-")}`}
      />
      <span
        className={`text-2xl font-bold ${isActive ? "text-white" : "text-slate-900"}`}
      >
        {count}
      </span>
      <span
        className={`text-xs font-medium ${isActive ? "text-slate-300" : "text-slate-500"}`}
      >
        {label}
      </span>
    </button>
  );
}

function HealthGridPage() {
  const navigate = useNavigate();
  const [riskFilter, setRiskFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);

  const { data: summary } = useQuery({
    queryKey: ["monitoring", "risk-summary"],
    queryFn: monitoringService.getRiskSummary,
    refetchInterval: () => backgroundDbWorkRefetchIntervalMs(60_000),
  });

  const { data: gridData, isLoading: gridLoading } = useQuery({
    queryKey: ["monitoring", "health-grid", riskFilter, searchQuery, page],
    queryFn: () =>
      monitoringService.getHealthGrid({
        riskLevel: riskFilter !== "all" ? riskFilter : undefined,
        search: searchQuery || undefined,
        page,
        limit: 25,
      }),
    refetchInterval: () => backgroundDbWorkRefetchIntervalMs(60_000),
  });

  const tenants = gridData?.data ?? [];
  const pagination = gridData?.pagination;

  const handleRiskFilter = (level: string) => {
    setRiskFilter(riskFilter === level ? "all" : level);
    setPage(1);
  };

  const totalTenants = summary
    ? summary.healthy +
      summary.monitor +
      summary.at_risk +
      summary.critical +
      summary.unscored
    : 0;

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Client Health Monitor"
        subtitle={`Real-time health overview for ${totalTenants} clients`}
      />

      {/* Risk Summary Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
        <SummaryCard
          label="Healthy"
          count={summary?.healthy ?? 0}
          riskLevel="healthy"
          isActive={riskFilter === "healthy"}
          onClick={() => handleRiskFilter("healthy")}
        />
        <SummaryCard
          label="Monitor"
          count={summary?.monitor ?? 0}
          riskLevel="monitor"
          isActive={riskFilter === "monitor"}
          onClick={() => handleRiskFilter("monitor")}
        />
        <SummaryCard
          label="At Risk"
          count={summary?.at_risk ?? 0}
          riskLevel="at_risk"
          isActive={riskFilter === "at_risk"}
          onClick={() => handleRiskFilter("at_risk")}
        />
        <SummaryCard
          label="Critical"
          count={summary?.critical ?? 0}
          riskLevel="critical"
          isActive={riskFilter === "critical"}
          onClick={() => handleRiskFilter("critical")}
        />
        <SummaryCard
          label="Unscored"
          count={summary?.unscored ?? 0}
          riskLevel="unscored"
          isActive={riskFilter === "unscored"}
          onClick={() => handleRiskFilter("unscored")}
        />
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1);
          }}
          className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-0"
        />
      </div>

      {/* Tenant Grid */}
      {gridLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" />
        </div>
      ) : tenants.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 py-20">
          <Activity className="h-10 w-10 text-slate-300" />
          <p className="mt-3 text-sm font-medium text-slate-500">
            No tenants match your filters
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/50">
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Health
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Client
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Plan
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <Users className="mx-auto h-3.5 w-3.5" />
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <Wifi className="mx-auto h-3.5 w-3.5" />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Risk
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Last Active
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {tenants.map((tenant) => (
                <TenantRow
                  key={tenant.id}
                  tenant={tenant}
                  onClick={() => navigate(`/tenants/${tenant.id}`)}
                />
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
              <span className="text-xs text-slate-500">
                Showing {(pagination.page - 1) * pagination.limit + 1}–
                {Math.min(pagination.page * pagination.limit, pagination.total)}{" "}
                of {pagination.total}
              </span>
              <div className="flex gap-1">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                >
                  Prev
                </button>
                <button
                  disabled={page >= pagination.totalPages}
                  onClick={() => setPage(page + 1)}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TenantRow({
  tenant,
  onClick,
}: {
  tenant: HealthGridTenant;
  onClick: () => void;
}) {
  const lastActive = tenant.lastActiveAt
    ? formatRelativeTime(new Date(tenant.lastActiveAt))
    : "Never";

  return (
    <tr
      onClick={onClick}
      className="cursor-pointer transition-colors hover:bg-slate-50/50"
    >
      <td className="px-4 py-3">
        <HealthScoreCircle score={tenant.healthScore} />
      </td>
      <td className="px-4 py-3">
        <div>
          <p className="text-sm font-semibold text-slate-900">{tenant.name}</p>
          <p className="text-xs text-slate-400">{tenant.email}</p>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
          {tenant.planName}
        </span>
      </td>
      <td className="px-4 py-3 text-center">
        <span className="text-sm font-medium text-slate-700">
          {tenant.userCount}
        </span>
      </td>
      <td className="px-4 py-3 text-center">
        {tenant.onlineUsers > 0 ? (
          <span className="inline-flex items-center gap-1 text-sm font-medium text-emerald-600">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            {tenant.onlineUsers}
          </span>
        ) : (
          <span className="text-sm text-slate-400">0</span>
        )}
      </td>
      <td className="px-4 py-3">
        <RiskBadge level={tenant.riskLevel} />
      </td>
      <td className="px-4 py-3">
        <span className="text-xs text-slate-500">{lastActive}</span>
      </td>
    </tr>
  );
}

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export default HealthGridPage;
